import {
    IProject,
    IProjectNextProjectDetails,
    IProjectProgress,
} from 'holberton-school-intranet-api';
import * as React from 'react';
import { ReactElement, useEffect, useState } from 'react';

import { get, post } from '../../api/utils';
import ErrorAlert from '../common/ErrorAlert';
import useInterval from '../lib/useInterval';
import { t } from '../../utils/i18n';

import ProjectEncouragement from './ProjectEncouragement';
import { IProjectPeerReview } from './ProjectReadyForReviewBody';
import ProjectReviewBody from './ProjectReviewBody';
import ProjectCircle from './pathway/ProjectCircle';

interface IProps {
    style: string;
    correction: {
        startReviewURI: string;
        statusURI: string;
    };
    csrfToken: string;
    hasManualChecks?: boolean;
    nextProject: {
        details: IProjectNextProjectDetails | null;
        fetchURI: string;
    };
    pollingInterval: number | null;
    peerReview: IProjectPeerReview | null;
    progress: IProjectProgress;
    project: IProject;
    skipURI: string;
    taskLevelReviewTypeHumanized: string | null;
}

export default function ProjectReview({
    correction,
    csrfToken,
    hasManualChecks = false,
    nextProject,
    pollingInterval,
    peerReview,
    progress,
    project,
    style,
    skipURI,
    taskLevelReviewTypeHumanized,
}: IProps): ReactElement {
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [refreshing, setRefreshing] = useState<boolean>(false);
    const [updatedProject, setUpdatedProject] = useState<IProject>(project);
    const [updatedProgress, setUpdatedProgress] = useState<IProjectProgress>(
        progress,
    );

    const isColumnStyle = style === 'column';

    const onProgressChanged = (e: CustomEvent<IProjectProgress>): void => {
        setUpdatedProgress(e.detail);
        setUpdatedProject((prev) => ({
            ...prev,
            completion: e.detail.summary.completion,
            score: e.detail.summary.score,
        }));
    };

    const refreshStatus = async (): Promise<void> => {
        if (
            refreshing ||
            updatedProgress.auto_review.inability_to_execute_reason !==
                'in_progress'
        ) {
            return;
        }

        try {
            setRefreshing(true);

            // Notify React components so they can do whatever they want
            document.dispatchEvent(
                new CustomEvent('project_progress_changed', {
                    detail: await get<IProjectProgress>(
                        correction.statusURI,
                        csrfToken,
                    ),
                }),
            );
        } catch (err) {
            setError(err.message);
        } finally {
            setRefreshing(false);
        }
    };

    useEffect(() => {
        document.addEventListener(
            'project_progress_changed',
            onProgressChanged,
        );

        return (): void => {
            document.removeEventListener(
                'project_progress_changed',
                onProgressChanged,
            );
        };
    }, []);

    useEffect(() => {
        updatedProgress.tasks.forEach((task) => {
            const {
                id,
                score_info: { passed, score, points },
                correction_requests_count,
            } = task;

            // Update task footer score
            const scoreElement = document.getElementById(
                `task-${id}-score-info-score`,
            );
            if (scoreElement) {
                scoreElement.parentElement.className = passed
                    ? 'text-success'
                    : 'text-danger';
                scoreElement.innerHTML = score.toString();
            }

            // Update points display
            const pointsElement = document.getElementById(
                `task-${id}-score-info-points`,
            );
            if (pointsElement) {
                pointsElement.innerHTML = `/${points}`;
            }

            // Calculate and update percentage
            const percentage =
                points > 0 ? ((score / points) * 100).toFixed(1) : '0';
            const percentageElement = document.getElementById(
                `task-${id}-score-info-percentage`,
            );
            if (percentageElement) {
                percentageElement.innerHTML = `${percentage}%`;
                // Update badge class based on passed status
                percentageElement.className = `label ${
                    passed ? 'label-success' : 'label-danger'
                }`;
            }

            // Update container styling (background and border colors)
            const containerElement = document.getElementById(
                `task-${id}-score-info-container`,
            );
            if (containerElement) {
                if (passed) {
                    containerElement.style.background =
                        'linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%)';
                    containerElement.style.borderColor = '#28a745';
                } else {
                    containerElement.style.background =
                        'linear-gradient(135deg, #f8d7da 0%, #f5c6cb 100%)';
                    containerElement.style.borderColor = '#dc3545';
                }
            }

            // Update correction requests count
            const nbRequests = correction_requests_count || 0;
            const requestsContainerElement = document.getElementById(
                `task-${id}-correction-requests-container`,
            );
            const requestsLabelElement = document.getElementById(
                `task-${id}-correction-requests-label`,
            );
            if (requestsContainerElement && requestsLabelElement) {
                if (nbRequests > 0) {
                    requestsContainerElement.style.display = 'block';
                    // Update label class based on count
                    const labelClass =
                        nbRequests > 3 ? 'label-danger' : 'label-success';
                    const pluralizedText =
                        nbRequests === 1
                            ? window.I18n.t(
                                  'tasks.correction_request',
                                  'correction request',
                              )
                            : window.I18n.t(
                                  'tasks.correction_requests',
                                  'correction requests',
                              );
                    // Rebuild the label content
                    requestsLabelElement.className = `label ${labelClass}`;
                    requestsLabelElement.innerHTML = `<span class="glyphicon glyphicon-exclamation-sign"></span> ${nbRequests} ${pluralizedText}`;
                } else {
                    requestsContainerElement.style.display = 'none';
                }
            }
        });
    }, [updatedProgress]);

    useInterval(() => {
        refreshStatus();
    }, pollingInterval);

    const onStartReview = async (): Promise<void> => {
        setError(null);
        setLoading(true);
        try {
            setUpdatedProgress(
                await post<IProjectProgress>(
                    correction.startReviewURI,
                    csrfToken,
                    {},
                ),
            );
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="panel panel-default">
            <div className="panel-heading">
                <h3 className="panel-title">{t('projects.review.score', 'Score')}</h3>
            </div>
            <div className="panel-body">
                <div className={`align-items-center gap-5 ${isColumnStyle ? '' : 'd-flex'}`}>
                    <div
                        className="pathway"
                        style={{ padding: '10px 30px 40px 30px' }}
                    >
                        <ProjectCircle
                            project={updatedProject}
                            scale={1.25}
                            withShadow={true}
                            placement="tile"
                            alwaysShowMandatoryScore={true}
                            hideTitle={true}
                        />
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center'}}>
                        <p className="mb-2 text-primary">
                            <ProjectEncouragement
                                project={updatedProject}
                                reviewTypeHumanized={
                                    taskLevelReviewTypeHumanized
                                }
                                hasManualChecks={hasManualChecks}
                                progress={updatedProgress}
                            />
                        </p>

                        {error && (
                            <div className="mb-2">
                                <ErrorAlert error={error} />
                            </div>
                        )}

                        <ProjectReviewBody
                            csrfToken={csrfToken}
                            fullManualReview={!taskLevelReviewTypeHumanized}
                            hasManualChecks={hasManualChecks}
                            loading={loading}
                            nextProjectDetails={nextProject.details}
                            onStartReview={onStartReview}
                            peerReview={peerReview}
                            progress={updatedProgress}
                            skipURI={skipURI}
                            project={updatedProject}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}
